import { useEffect, useId, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { api, ErrorDeApi, type Cliente, type ResultadoDeInvitacion, type Usuario } from '../servicios/api'
import { useAvisos } from '../componentes/Toast'
import { explicarInvitacion } from '../componentes/explicarInvitacion'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { ConsentimientosDeSoporte } from './ConsentimientosDeSoporte'
import { PestanasDeAdministracion } from './Bitacora'
import { telon } from '../ganchos/telon'

const ROLES = ['Administrador', 'Contador', 'Auxiliar', 'SoloLectura'] as const

const ETIQUETA_ROL: Record<string, string> = {
  Administrador: 'Administrador',
  Contador: 'Contador',
  Auxiliar: 'Auxiliar',
  SoloLectura: 'Solo lectura',
}

/**
 * U1 · El rol se lee mucho más de lo que se cambia, y un `<select>` en cada fila lo escondía
 * entre controles. La jerarquía va por intensidad del acento, igual que el resto del sistema.
 */
const INSIGNIA_DE_ROL: Record<string, string> = {
  Administrador: 'rol',
  Contador: 'aviso',
  Auxiliar: 'suave',
  SoloLectura: 'vigente',
}

/**
 * U3 · «Invitado» no puede pintarse igual que «Activo». Es alguien que todavía no ha entrado
 * —y a quien quizá nunca le llegó el correo—, así que el administrador tiene que verlo de un
 * vistazo para saber a quién le falta activar.
 */
const INSIGNIA_DE_ESTADO: Record<string, string> = {
  Activo: 'vigente',
  Invitado: 'aviso',
  Revocado: 'vencido',
}

/**
 * U7 · «hoy 08:00» y «ayer 17:12» se ubican de un vistazo; una fecha completa obliga a
 * compararla con la de hoy. Más atrás de ayer la fecha sí es la información útil.
 */
export function ultimoAcceso(iso?: string): string {
  if (!iso) return 'Nunca'
  const fecha = new Date(iso)
  const hora = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
  const dia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diferencia = (dia(new Date()) - dia(fecha)) / 86_400_000
  if (diferencia === 0) return `hoy ${hora}`
  if (diferencia === 1) return `ayer ${hora}`
  return fecha.toLocaleDateString('es-MX')
}

/** Gestión de colaboradores del despacho: invitar, cambiar rol, revocar. SRS: RF-104. Cédula: I-02. */
export function Usuarios() {
  const { instance } = useMsal()
  const correoPropio = instance.getActiveAccount()?.username
  // Sin este `undefined` la pantalla no podía distinguir «el despacho no tiene colaboradores»
  // de «la consulta falló»: ante un fallo mostraba el error y, debajo, «Aún no hay
  // colaboradores», que es una afirmación distinta y falsa.
  const [usuarios, setUsuarios] = useState<Usuario[]>()
  const [error, setError] = useState('')
  const [mostrarInvitar, setMostrarInvitar] = useState(false)
  const [cambiandoRolDe, setCambiandoRolDe] = useState<string | null>(null)
  const [editandoRolDe, setEditandoRolDe] = useState<string | null>(null)
  const [gestionandoCarteraDe, setGestionandoCarteraDe] = useState<Usuario | null>(null)
  const [reenviandoA, setReenviandoA] = useState<string | null>(null)
  const [cerrandoSesiones, setCerrandoSesiones] = useState(false)
  // #149 · Lo que el administrador tiene que hacer a mano para que el invitado entre. No va en un
  // aviso: se desvanece antes de que alguien termine de leer qué hacer.
  const [pendienteDeCuenta, setPendienteDeCuenta] = useState<string | null>(null)
  const avisar = useAvisos()

  function contarInvitacion(correo: string, resultado: ResultadoDeInvitacion) {
    const { aviso, pendiente } = explicarInvitacion(correo, resultado)
    if (aviso) avisar(aviso)
    setPendienteDeCuenta(pendiente)
  }

  async function recargar() {
    try {
      setUsuarios(await api.listarUsuarios())
      setError('')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la lista de colaboradores.')
    }
  }

  useEffect(() => {
    void recargar()
  }, [])

  /**
   * U3 · Reenviar la invitación. El resultado se dice con un aviso y no en silencio: el
   * administrador necesita saber si el correo salió, porque el motivo de estar reenviando es
   * justamente que el primero no llegó. `correoEnviado === false` no es un error —la
   * invitación sigue vigente— pero sí es algo que hay que contar. Reenviar también reintenta
   * crear la cuenta del CIAM si la primera vez falló (#149).
   */
  async function reenviar(usuario: Usuario) {
    setError('')
    setReenviandoA(usuario.id)
    try {
      contarInvitacion(usuario.correo, await api.reenviarInvitacion(usuario.id))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo reenviar la invitación.')
    } finally {
      setReenviandoA(null)
    }
  }

  /** U9 · La acción que sí existe: invalidar todo token emitido antes de este instante. */
  async function cerrarOtrasSesiones() {
    setError('')
    setCerrandoSesiones(true)
    try {
      await api.cerrarOtrasSesiones()
      avisar('Tus otras sesiones quedaron cerradas. En esas pantallas habrá que volver a entrar.')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron cerrar las otras sesiones.')
    } finally {
      setCerrandoSesiones(false)
    }
  }

  async function cambiarRol(usuario: Usuario, rol: string) {
    setError('')
    setCambiandoRolDe(usuario.id)
    try {
      await api.cambiarRolUsuario(usuario.id, rol)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el rol.')
    } finally {
      setCambiandoRolDe(null)
    }
  }

  const [revocando, setRevocando] = useState<Usuario | null>(null)

  async function revocar(usuario: Usuario) {
    setRevocando(null)
    setError('')
    try {
      await api.revocarUsuario(usuario.id)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo revocar el acceso.')
    }
  }

  return (
    <section>
      <header className="encabezado">
        <h1>Bitácora y usuarios</h1>
        <span className="encabezado-acciones">
          <button className="primario" onClick={() => setMostrarInvitar(true)}>Invitar usuario</button>
        </span>
      </header>
      <p className="sutil">Auditoría por despacho · hora local · consultable por el administrador</p>

      <PestanasDeAdministracion activa="usuarios" />

      {error && <p className="error" role="alert">{error}</p>}
      {pendienteDeCuenta && (
        <div className="aviso-critico" role="status">
          <span>{pendienteDeCuenta}</span>
          <button className="enlace" onClick={() => setPendienteDeCuenta(null)}>Entendido</button>
        </div>
      )}

      <div className="tabla-envoltura">
      <table className="tabla">
        <thead>
          <tr>
            {/* U8 · Nombre y correo en una celda, igual que la tabla de Clientes: son el
                mismo dato —quién es— y en dos columnas obligaban a leer en zigzag. */}
            <th>Usuario</th>
            <th>Rol</th>
            <th>Estado</th>
            <th className="num">Clientes</th>
            <th>MFA</th>
            <th>Último acceso</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(usuarios ?? []).map((u) => {
            const revocado = u.estado === 'Revocado'
            const esPropio = u.correo === correoPropio?.toLowerCase()
            return (
              <tr key={u.id} style={{ opacity: revocado ? 0.5 : 1 }}>
                <td>
                  <div style={{ fontWeight: 600 }}>{u.nombre}{esPropio ? ' (tú)' : ''}</div>
                  <div className="secundaria" style={{ fontSize: 11 }}>
                    <span className="mono">{u.correo}</span>
                  </div>
                </td>
                <td>
                  {editandoRolDe === u.id ? (
                    <select
                      value={u.rol}
                      autoFocus
                      disabled={cambiandoRolDe === u.id}
                      onBlur={() => setEditandoRolDe(null)}
                      onChange={(e) => void cambiarRol(u, e.target.value)}
                      aria-label={`Rol de ${u.nombre}`}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{ETIQUETA_ROL[r]}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      className="enlace"
                      disabled={revocado}
                      onClick={() => setEditandoRolDe(u.id)}
                      aria-label={`Rol de ${u.nombre}: ${ETIQUETA_ROL[u.rol]}. Cambiar`}
                    >
                      <span className={`insignia ${INSIGNIA_DE_ROL[u.rol] ?? 'neutra'}`}>
                        {ETIQUETA_ROL[u.rol] ?? u.rol}
                      </span>
                    </button>
                  )}
                </td>
                {/* U3 · «Invitado» no es un estado verde: es alguien que todavía no entra y
                    a quien quizá nunca le llegó el correo. Se distingue de «Activo» para que
                    el administrador vea de un vistazo a quién le falta activar. */}
                <td>
                  <span className={`insignia ${INSIGNIA_DE_ESTADO[u.estado] ?? 'neutra'}`}>{u.estado}</span>
                </td>
                {/* U2 · El conteo de cartera. Para el Administrador el servidor manda NULO, no
                    cero, porque ve toda la cartera: aquí se escribe «todos», que es el dato. */}
                <td className="num">
                  {u.clientesAsignados === null
                    ? <span className="sutil">todos</span>
                    : u.clientesAsignados}
                </td>
                <td>
                  {u.mfaHabilitado ? 'Activo' : <span className="sutil">pendiente</span>}
                </td>
                <td className="sutil">{ultimoAcceso(u.ultimoAccesoUtc)}</td>
                <td>
                  <span className="acciones-de-fila">
                    {u.estado === 'Invitado' && (
                      <button
                        className="enlace"
                        disabled={reenviandoA === u.id}
                        onClick={() => void reenviar(u)}
                      >
                        {reenviandoA === u.id ? 'Reenviando…' : 'Reenviar invitación'}
                      </button>
                    )}
                    {!revocado && u.rol !== 'Administrador' && (
                      <button className="enlace" onClick={() => setGestionandoCarteraDe(u)}>Cartera</button>
                    )}
                    {!revocado && !esPropio && (
                      <button className="enlace" onClick={() => setRevocando(u)}>Revocar</button>
                    )}
                  </span>
                </td>
              </tr>
            )
          })}
          {/* «No hay» y «no se pudo cargar» son dos afirmaciones distintas; antes salían las
              dos a la vez ante un fallo, y una era falsa. */}
          {usuarios?.length === 0 && (
            <tr>
              <td colSpan={7} className="vacio">Aún no hay usuarios en este despacho.</td>
            </tr>
          )}
          {usuarios === undefined && !error && (
            <tr>
              <td colSpan={7} className="vacio">Cargando…</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {mostrarInvitar && (
        <FormularioDeInvitacion
          onCerrar={() => setMostrarInvitar(false)}
          onInvitado={(correo, resultado) => {
            setMostrarInvitar(false)
            contarInvitacion(correo, resultado)
            void recargar()
          }}
        />
      )}

      {gestionandoCarteraDe && (
        <CarteraModal usuario={gestionandoCarteraDe} onCerrar={() => setGestionandoCarteraDe(null)} />
      )}

      {/* U4 · `window.confirm` no dice qué se conserva ni qué se pierde, y esta es la acción
          más destructiva de la pantalla. Mismo tratamiento que en Contabilización y en el
          Expediente. */}
      {revocando && (
        <ModalDeRevocacion
          usuario={revocando}
          onCerrar={() => setRevocando(null)}
          onConfirmar={() => void revocar(revocando)}
        />
      )}

      <p className="nota">
        Roles: administrador, contador, auxiliar y solo lectura · la cartera de clientes se
        asigna por usuario · la revocación es inmediata y queda en la bitácora.
      </p>

      {/* U9 · El hallazgo pedía una lista de «sesiones activas». NO SE PUEDE Y NO SE INVENTA:
          los tokens son de Entra y no se guardan en ninguna parte, así que ContaQuick no tiene
          cómo saber cuántas sesiones hay abiertas. Un número ahí sería inventado.

          Lo que sí existe desde RF-105 es la marca de revocación: cerrar las demás sesiones
          invalida todo token emitido antes de este instante. Llevaba desde entonces sin una
          sola forma de invocarse desde la interfaz. Eso es lo que se entrega: la acción real,
          no el número que no tenemos. */}
      <section className="tarjeta" style={{ marginTop: 'var(--space-4)' }}>
        <div className="tarjeta-cabecera">
          <h2>Tu sesión</h2>
          <button className="enlace" disabled={cerrandoSesiones} onClick={() => void cerrarOtrasSesiones()}>
            {cerrandoSesiones ? 'Cerrando…' : 'Cerrar mis otras sesiones'}
          </button>
        </div>
        <p className="nota" style={{ margin: 0 }}>
          Si entraste desde una computadora que ya no controlas, esto invalida de inmediato
          todas tus sesiones menos esta y obliga a volver a iniciar sesión en las demás.
          ContaQuick no puede listarte cuáles están abiertas: las sesiones viven en Microsoft,
          no aquí.
        </p>
      </section>

      <ConsentimientosDeSoporte />
    </section>
  )
}

/** Confirmación de revocación de acceso (RF-104). */
function ModalDeRevocacion({ usuario, onCerrar, onConfirmar }: {
  usuario: Usuario
  onCerrar: () => void
  onConfirmar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo}>Revocar el acceso de {usuario.nombre}</h2>
        <p>
          Deja de entrar de inmediato y sus sesiones abiertas se cierran. Lo que ya hizo no se
          toca: sus pólizas, sus envíos y sus asientos de bitácora siguen donde están, a su
          nombre.
        </p>
        <p className="nota">Se le puede volver a invitar después. La revocación queda en la bitácora.</p>
        <div className="acciones">
          <button onClick={onCerrar}>Cancelar</button>
          <button className="primario" onClick={onConfirmar}>Revocar acceso</button>
        </div>
      </div>
    </div>
  )
}

/** Cartera asignada a un colaborador: acota qué clientes ve si no es Administrador. Cédula: I-02, A-05. */
function CarteraModal({ usuario, onCerrar }: { usuario: Usuario; onCerrar: () => void }) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    Promise.all([api.listarClientes({}), api.clientesAsignados(usuario.id)])
      .then(([listaClientes, asignados]) => {
        setClientes(listaClientes.items)
        setSeleccionados(new Set(asignados))
      })
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la cartera.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.id])

  function alternar(clienteId: string) {
    setSeleccionados((actual) => {
      const nuevo = new Set(actual)
      if (nuevo.has(clienteId)) nuevo.delete(clienteId)
      else nuevo.add(clienteId)
      return nuevo
    })
  }

  async function guardar() {
    setError('')
    setGuardando(true)
    try {
      await api.asignarClientes(usuario.id, [...seleccionados])
      onCerrar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo guardar la cartera asignada.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 94vw)' }}
      >
        <h2 id={idTitulo}>Cartera de {usuario.nombre}</h2>
        <p className="sutil">
          Sin clientes marcados, este colaborador no ve ningún cliente del despacho (RF-104, cédula I-02).
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--color-neutral-200)', borderRadius: 8 }}>
          {clientes.length === 0 ? (
            <p className="vacio">Aún no hay clientes en la cartera del despacho.</p>
          ) : (
            clientes.map((c) => (
              <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px' }}>
                <input
                  type="checkbox"
                  checked={seleccionados.has(c.id)}
                  onChange={() => alternar(c.id)}
                />
                <span className="mono">{c.rfc}</span> · {c.razonSocial}
              </label>
            ))
          )}
        </div>

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="button" className="primario" disabled={guardando} onClick={() => void guardar()}>
            {guardando ? 'Guardando…' : 'Guardar cartera'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FormularioDeInvitacion({ onCerrar, onInvitado }: {
  onCerrar: () => void
  onInvitado: (correo: string, resultado: ResultadoDeInvitacion) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    const datos = new FormData(evento.currentTarget)

    const correo = String(datos.get('correo'))

    try {
      onInvitado(correo, await api.invitarUsuario({
        nombre: String(datos.get('nombre')),
        correo,
        rol: String(datos.get('rol')),
      }))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo invitar al colaborador.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        {/* U5 · El botón que abre este modal dice «Invitar usuario» desde que se unificó el
            término; el título se quedó con el anterior, así que la misma acción tenía dos
            nombres en dos pantallazos seguidos. */}
        <h2 id={idTitulo}>Invitar usuario</h2>

        <label>
          Nombre
          <input name="nombre" required maxLength={200} placeholder="Nombre y apellidos" />
        </label>

        <label>
          Correo
          <input name="correo" type="email" required maxLength={320} placeholder="colaborador@despacho.mx" />
        </label>

        <label>
          Rol
          <select name="rol" defaultValue="Contador" required>
            {ROLES.map((r) => (
              <option key={r} value={r}>{ETIQUETA_ROL[r]}</option>
            ))}
          </select>
        </label>

        <p className="sutil">
          El colaborador activa su cuenta solo: al iniciar sesión con este mismo correo, queda vinculado al
          despacho automáticamente.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Invitando…' : 'Invitar'}
          </button>
        </div>
      </form>
    </div>
  )
}
